/**
 * xelara-scroll-row.js — BUILD-ORDER C5
 * Prev/next scrollBy, dots → snap, progress from scrollLeft, centre-snap init,
 * optional active-card via nearest-to-centre (data-xel-scroll-active).
 * Vanilla ESM, no deps.
 */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const smooth = () => (reduce.matches ? 'auto' : 'smooth');
const kids = (row) => [...row.children].filter((el) => el.nodeType === 1);
const maxScroll = (row) => Math.max(0, row.scrollWidth - row.clientWidth);

function step(row) {
  const el = kids(row)[0];
  if (!el) return row.clientWidth * 0.8;
  return el.getBoundingClientRect().width + (parseFloat(getComputedStyle(row).gap) || 0);
}

function nearest(row) {
  const mid = row.scrollLeft + row.clientWidth / 2;
  let best = 0;
  let dist = Infinity;
  kids(row).forEach((el, i) => {
    const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid);
    if (d < dist) {
      dist = d;
      best = i;
    }
  });
  return best;
}

function goTo(row, index) {
  const list = kids(row);
  const el = list[Math.max(0, Math.min(index, list.length - 1))];
  if (!el) return;
  row.scrollTo({
    left: el.offsetLeft - (row.clientWidth - el.offsetWidth) / 2,
    behavior: smooth(),
  });
}

// User-initiated input. Anything else (scrollTo, scrollend) is programmatic.
const USER_EVENTS = ['wheel', 'pointerdown', 'touchstart', 'keydown'];

function centreInit(row) {
  if (row.dataset.xelScrollSnap !== 'center') return;
  const list = kids(row);
  if (list.length < 2) return;
  // RESPONSIVE-PLAN §3/§8: touch widths snap to start, and the CSS says so.
  // Read it from the first card instead of duplicating the breakpoint here.
  if (getComputedStyle(list[0]).scrollSnapAlign.split(' ')[0] !== 'center') return;
  const mid = (list.length - 1) / 2;
  const a = list[Math.floor(mid)];
  const b = list[Math.ceil(mid)];
  const target =
    (a.offsetLeft + a.offsetWidth / 2 + b.offsetLeft + b.offsetWidth / 2) / 2 - row.clientWidth / 2;
  // With an even count the midpoint sits between two tiles, and mandatory
  // snapping would immediately pull it onto one of them. Suspend snapping
  // until the first user gesture so both edges peek symmetrically on load.
  row.style.scrollSnapType = 'none';
  row.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
  const restore = () => {
    row.style.scrollSnapType = '';
    USER_EVENTS.forEach((t) => row.removeEventListener(t, restore));
  };
  USER_EVENTS.forEach((t) => row.addEventListener(t, restore, { passive: true }));
}

function initRow(row) {
  const root = row.closest('[data-xel-scroll-root]') || row.parentElement;
  const prev = root.querySelector('[data-xel-scroll-prev]');
  const next = root.querySelector('[data-xel-scroll-next]');
  const progress = root.querySelector('[data-xel-scroll-progress]');
  const dots = [...root.querySelectorAll('[data-xel-scroll-dot]')];
  const wantActive = row.hasAttribute('data-xel-scroll-active');
  // A dot click locks its index: while the card grows, layout shifts and
  // nearest() can drift a slot, and a card near either end can never reach
  // the centre at all. The lock holds until the user scrolls by hand.
  let locked = null;

  const paint = (i) => {
    if (dots.length) dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    if (wantActive) kids(row).forEach((el, n) => el.classList.toggle('is-active', n === i));
  };

  const sync = () => {
    const max = maxScroll(row);
    if (prev) {
      const start = row.scrollLeft <= 1;
      prev.disabled = start;
      prev.classList.toggle('is-active', !start);
    }
    if (next) {
      const end = row.scrollLeft >= max - 1;
      next.disabled = end;
      next.classList.toggle('is-active', !end);
    }
    if (progress) {
      const p = max ? Math.min(1, Math.max(0, row.scrollLeft / max)) : 1;
      progress.style.setProperty('--xel-scroll-progress', String(p));
    }
    paint(locked ?? nearest(row));
  };

  const unlock = () => {
    locked = null;
  };
  USER_EVENTS.forEach((t) => row.addEventListener(t, unlock, { passive: true }));

  prev?.addEventListener('click', () => {
    unlock();
    row.scrollBy({ left: -step(row), behavior: smooth() });
  });
  next?.addEventListener('click', () => {
    unlock();
    row.scrollBy({ left: step(row), behavior: smooth() });
  });
  dots.forEach((d, i) =>
    d.addEventListener('click', () => {
      locked = i;
      paint(i);
      goTo(row, i);
    })
  );
  row.addEventListener('scroll', sync, { passive: true });
  row.addEventListener('scrollend', sync);
  row.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    row.scrollBy({ left: (e.key === 'ArrowRight' ? 1 : -1) * step(row), behavior: smooth() });
  });
  if (!row.hasAttribute('tabindex')) row.tabIndex = 0;

  centreInit(row);
  sync();
}

export function init(scope = document) {
  scope.querySelectorAll('[data-xel-scroll]').forEach(initRow);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  // Theme editor and `theme dev` hot reload re-render a section's DOM without
  // re-running module scripts; re-initialise inside the replaced section.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
}
